import { Card, Dropdown, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Scrollbar from '../../../components/base/Scrollbar';
import AlertItem from '../../alerts/AlertItem';
import { useAlerts, useAckAlert } from '../../../features/alerts/useAlerts';
import { useAuthContext } from '../../../providers/AuthProvider';

/**
 * Live factory alerts. Previously this rendered a fixed demo list, which is
 * worse than showing nothing: a bell that never changes teaches people the bell
 * means nothing.
 *
 * Every row here is a condition that is true right now — the evaluator closes
 * them by itself when the machine recovers, so an empty list is a real statement
 * about the factory rather than a list nobody has tidied.
 */
const NotificationDropdownMenu = ({ className }) => {
  const { data, isLoading } = useAlerts({ status: 'OPEN,ACKNOWLEDGED', limit: 20 });
  const ack = useAckAlert();
  // useAuthContext returns [state, dispatch, helpers]
  const [authState] = useAuthContext();

  const alerts = data?.data || [];
  const counts = data?.counts || {};

  const onAck = alert =>
    ack.mutate({
      id: alert._id,
      actor: authState?.user?.name || authState?.user?.username || null
    });

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        'navbar-dropdown-caret py-0 notification-dropdown-menu shadow border border-300'
      )}
    >
      <Card className="position-relative border-0">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="text-black mb-0">Alerts</h5>
            <span className="fs-10 text-700">
              {counts.CRITICAL ? (
                <span className="text-danger fw-semibold me-2">{counts.CRITICAL} critical</span>
              ) : null}
              {counts.WARNING ? (
                <span className="text-warning fw-semibold">{counts.WARNING} warning</span>
              ) : null}
            </span>
          </div>
        </Card.Header>

        <Card.Body className="p-0" style={{ height: '27rem' }}>
          <Scrollbar>
            {isLoading ? (
              <div className="d-flex align-items-center justify-content-center py-6 gap-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="fs-9 text-700">Loading…</span>
              </div>
            ) : !alerts.length ? (
              <div className="text-center py-6 text-700 fs-9">
                Nothing needs attention.
                <div className="fs-10 text-600 mt-1">
                  Alerts appear here on their own and close themselves when the
                  machine recovers.
                </div>
              </div>
            ) : (
              alerts.map((a, i) => (
                <AlertItem
                  key={a._id}
                  alert={a}
                  onAck={onAck}
                  compact
                  className={classNames({ 'border-bottom': i !== alerts.length - 1 })}
                />
              ))
            )}
          </Scrollbar>
        </Card.Body>

        <Card.Footer className="p-0 border-top">
          <div className="my-2 text-center fw-bold fs-10 text-600 d-flex justify-content-center gap-3">
            <Link to="/apps/machine-management/notifications" className="fw-bolder">
              Notification history
            </Link>
            <span className="text-body-quaternary">·</span>
            <Link to="/apps/machine-management/factory-alerts" className="fw-bolder">
              Analytics
            </Link>
          </div>
        </Card.Footer>
      </Card>
    </Dropdown.Menu>
  );
};

export default NotificationDropdownMenu;
